import { fetchWithAdmin } from './fetchHelper';

/** React-query key prefix for the ledger read. Shared by the list (which owns
 *  the full key: prefix + subject + filters + page + view) and the findings
 *  page (which patches every cached page under the prefix on a resolution
 *  save, rather than knowing which one page-and-filter combo the reader came
 *  from). */
export const LEDGER_QUERY_KEY = 'question-quality-ledger';

/**
 * Client for the Question Quality read surface (Monitoring → Question Quality
 * tab + per-question pill). Backed by src/admin/monitoring/questionQualityApi.ts.
 * Read-only — verdicts are produced by the offline scripts/quality tooling.
 */

export type QVerdict = 'pass' | 'fail' | 'unsure';

/** What a human decided about ONE finding. `active` is the absence of a
 *  decision, so an unresolved finding carries no `resolution` at all. */
export type ResolutionStatus = 'active' | 'investigating' | 'fix-needed-by-ai'
    | 'review-pending-after-ai-update' | 'fixed' | 'verified-fixed' | 'not-an-issue';

export interface Resolution {
    status: ResolutionStatus;
    comment?: string;
    by?: string;
    /** `by` resolved to a display name, server-side. Absent for a system actor,
     *  or a user the lookup could not find — fall back to `by`. */
    byName?: string;
    at: string;
    /** Stamped by the sweep when a re-judge of the EDITED question passed —
     *  what makes `verified-fixed` different from `fixed`. `by`/`at` stay the
     *  closer's: it is still their decision, with evidence added. */
    verifiedAt?: string;
    verifiedByRun?: string;
}

/**
 * One superseded cycle of a finding — what a rule said about a version of the
 * question that no longer exists, and what was done about it.
 *
 * The ledger keeps one document per (question, rule) and replaces it on every
 * re-judge, so without this the record of a fix vanished at the moment the fix
 * was confirmed. Written by the offline recorder; read-only here.
 *
 * See docs/FeatureDesigns/question-quality/checks-target.md §11a.
 */
export interface PriorFinding {
    checkedAt: string;
    checkRunId: string;
    model: string;
    ruleVersion: string;
    contentHash: string;
    questionTs: number;
    verdict: QVerdict;
    severity: QSeverity;
    rationale: string;
    /** Absent when the live finding still carries this decision — one live
     *  status, never two copies of it. */
    resolution?: Resolution;
    aiFix?: AiFix;
}

/**
 * What the AI fixer did to this question, when a finding went through it.
 *
 * Written by the offline `scripts/quality` tooling, read-only here.
 * `revisionSeq` is what makes the card worth anything: it lets a reviewer open
 * the actual before/after rather than trusting the note's account of it.
 */
export interface AiFix {
    attemptedAt: string;
    model: string;
    runId?: string;
    revisionSeq?: number;
    changedFields: string[];
    changeNote: string;
    /** The fixer's own working, arrived at independently of the judge. */
    rationale?: string;
    outcome: 'fixed' | 'partial' | 'cannot-fix';
    /** The second agent's independent verdict on the patch. */
    checkVerdict?: 'agree' | 'disagree' | 'unsure';
}

/** Worst-first, matching the server's roll-up: a question with one finding
 *  nobody has ruled on reads `active`, whatever else has been settled. The order
 *  is the lifecycle read backwards — keep it in step with the server's copy. */
export const RESOLUTION_RANK: Record<ResolutionStatus, number> = {
    active: 7, investigating: 6, 'fix-needed-by-ai': 5,
    'review-pending-after-ai-update': 4, fixed: 3, 'verified-fixed': 2, 'not-an-issue': 1,
};

/** The lifecycle in order, worst first. The dropdown, the filter and the badge
 *  all read this list, so a state added here appears in all three. */
export const RESOLUTION_STATUSES: ResolutionStatus[] = [
    'active', 'investigating', 'fix-needed-by-ai',
    'review-pending-after-ai-update', 'fixed', 'verified-fixed', 'not-an-issue',
];

export const RESOLUTION_LABEL: Record<ResolutionStatus, string> = {
    active: 'Active',
    investigating: 'Investigating',
    'fix-needed-by-ai': 'Fix needed by AI',
    'review-pending-after-ai-update': 'Review pending after AI update',
    fixed: 'Fixed',
    'verified-fixed': 'Verified fixed',
    'not-an-issue': 'Not an issue',
};

/** Statuses the sweep derives. A reader may see one; nobody may set one. */
export const DERIVED_STATUSES: ResolutionStatus[] = ['verified-fixed'];
export type QSeverity = 'critical' | 'major' | 'minor';

export interface QualityVerdict {
    id: string;
    subject: string;
    questionId: string;
    ruleId: string;
    ruleVersion: string;
    verdict: QVerdict;
    severity: QSeverity;
    rationale: string;
    evidence?: Record<string, unknown>;
    model: string;
    checkedAt: string;
    checkRunId: string;
    resolution?: Resolution;
    aiFix?: AiFix;
    /** Every superseded cycle of this finding, newest first. */
    priorFindings?: PriorFinding[];
    /** Cycles that fell off the end of the stored cap. */
    priorFindingsDropped?: number;
    /** Whether this verdict's RULE may be routed to the AI fixer, from the rule
     *  catalogue. Absent on an older backend. */
    aiFixable?: { allowed: boolean; reason: string };
    /** Stem of the judged question, trimmed server-side. Absent when the
     *  question has since been deleted. */
    questionText?: string;
}

/** A rule's own definition, from the backend's catalogue in
 *  `src/quality/rules/`. Absent only for a verdict whose rule has since been
 *  deleted from it. */
export interface QualityRuleInfo {
    ruleId: string;
    /** One line for a human. `rubric` is the judge's prompt. */
    summary: string;
    version: number;
    effectiveVersion: string;
    severity: QSeverity;
    applies: { subjects?: string[]; questionTypes?: string[]; requires?: string[]; hashFields: string[] };
    rubric: string;
}

/** A rule's own definition, from the backend's catalogue in `src/quality/rules/`. */
export interface QualityRuleInfo {
    ruleId: string;
    /** One line for a human. `rubric` is the judge's prompt. */
    summary: string;
    version: number;
    effectiveVersion: string;
    severity: QSeverity;
    applies: { subjects?: string[]; questionTypes?: string[]; requires?: string[]; hashFields: string[] };
    rubric: string;
}

/** One rule's verdict for one question. `rationale` and `resolution` are carried
 *  for the cells a reader can act on: every non-pass, plus a pass that carries a
 *  decision (a `verified-fixed` finding IS a pass). */
export interface QualityCell {
    verdict: QVerdict;
    severity: QSeverity;
    rationale?: string;
    resolution?: Resolution;
    /** Superseded cycles behind this finding. The count only — the history is
     *  read on the findings page. */
    priorCount?: number;
}

/**
 * ONE question, everything judged about it — the unit both views are built from.
 *
 * By Question renders these as rows; By Rule counts the same (filtered) set per
 * rule. One population, so a filter means the same thing in both.
 */
export interface LedgerQuestion {
    questionId: string;
    /** Trimmed stem — flagged questions only, which are the only rows shown. */
    questionText?: string;
    questionType?: string;
    moduleId?: string;
    importStatus?: string;
    boards?: string[];
    grades?: string[];
    /** Owning tenant, carried only when it is NOT the public library — the
     *  server omits `'0'`, so absence IS "public library" here. */
    tenantId?: string;
    tenantSlug?: string;
    /** The teacher who authored it, if anyone did. Orthogonal to the tenant: a
     *  teacher question usually sits inside one. */
    teacherId?: string;
    teacherName?: string;
    cells: Record<string, QualityCell>;
    failCount: number;
    unsureCount: number;
    worstSeverity?: QSeverity;
    resolutionStatus: ResolutionStatus;
    activeCount: number;
    /** Passing cells that still carry a decision — a confirmed fix, or an AI fix
     *  whose review is outstanding. Why such a question is still listed. */
    resolvedPassCount: number;
    issueDetectedAt?: string;
    lastCheckedAt: string;
}

/**
 * Ownership buckets for the Owner filter.
 *
 * They OVERLAP, deliberately — a teacher's question inside a tenant is both
 * `tenant` and `teacher`, and each of those is a real question to ask of the
 * ledger: "what is wrong with this school's content" and "what is wrong with
 * what teachers write" are different reviews. `public` is the one exclusive
 * bucket, because "the shared library, as we authored it" is only true of a
 * question nobody else has a claim on. `public-owned` is the rest of tenant '0':
 * a teacher's or a student's own question, stored there too.
 */
export type QuestionOwner = 'public' | 'public-owned' | 'tenant' | 'teacher';

export const OWNERS: QuestionOwner[] = ['public', 'public-owned', 'tenant', 'teacher'];

export const OWNER_LABEL: Record<QuestionOwner, string> = {
    public: 'Public (without owner)',
    'public-owned': 'Public (with owner)',
    tenant: 'Tenant',
    teacher: 'Teacher',
};

/** A question's own publish state — distinct from any check's verdict on it. */
export type ImportStatus = 'live' | 'draft';

export const IMPORT_STATUSES: ImportStatus[] = ['live', 'draft'];

export const IMPORT_STATUS_LABEL: Record<ImportStatus, string> = {
    live: 'Live',
    draft: 'Draft',
};

/** Is this question in that bucket? See `QuestionOwner` for why more than one
 *  can be true at once. */
export function isOwnedBy(
    q: Pick<LedgerQuestion, 'tenantId' | 'teacherId'>,
    owner: QuestionOwner,
): boolean {
    switch (owner) {
        // The public library, and authored there rather than by a teacher — the
        // tenant is absent because the server omits '0'.
        case 'public': return !q.tenantId && !q.teacherId;
        case 'public-owned': return !q.tenantId && !!q.teacherId;
        case 'tenant': return !!q.tenantId;
        case 'teacher': return !!q.teacherId;
    }
}

/** Questions, each in exactly one bucket, worst-first. */
export interface QuestionTotals {
    checked: number; clean: number;
    critical: number; major: number; minor: number; unsure: number;
}

/** Per-rule tallies over every question the filters matched — not the page. */
export interface RuleCounts {
    ruleId: string;
    pass: number;
    unsure: number;
    fail: number;
    failBySeverity: Record<QSeverity, number>;
}

export interface LedgerResponse {
    subject: string;
    /** Every rule with a verdict here, alphabetical — the stable column set. */
    rules: string[];
    ruleInfo: QualityRuleInfo[];
    /** ONE PAGE of the filtered questions. Filtering and paging are the server's
     *  now: doing them here meant doing them to whatever had survived its cut. */
    questions: LedgerQuestion[];
    ruleCounts: RuleCounts[];
    /** Questions bucketed by their worst finding, over the whole filtered set. */
    totals: QuestionTotals;
    /** Questions matching the filters, across every page. */
    total: number;
    /**
     * The POOL — every question the subject has at the chosen board and grade,
     * judged or not, teacher-authored included.
     *
     * The denominator `total` and `totals.checked` are a fraction OF. Those two
     * are counted off the verdict ledger, so they are identical to each other by
     * construction: a question nobody has judged does not exist to that index.
     * Narrowed by board and grade only — every other filter on the bar is a fact
     * about a verdict.
     */
    poolTotal: number;
    page: number;
    pageSize: number;
    /** The verdict scan hit its ceiling — this subject's ledger is bigger than
     *  one read of it, so the counts describe a prefix of it. */
    truncated: boolean;
    lastCheckedAt?: string;
}

/**
 * Patch ONE cell's resolution into an already-fetched ledger row, and recompute
 * the two aggregate fields that depend on resolution — without refetching.
 *
 * `cells`/`failCount`/`unsureCount`/`worstSeverity` come from the JUDGE, not the
 * resolution, so an edited resolution never changes them; only
 * `resolutionStatus` and `activeCount` do, and both are cheap to recompute here
 * from the row's own (already-correct) `cells`. This is what lets the findings
 * page update its one row in the list behind it instead of the list reloading
 * everything to reflect a single saved comment.
 *
 * Mirrors `RESOLUTION_RANK` / the fixed→active downgrade in
 * `Backend/functions/src/admin/monitoring/questionQualityApi.ts` — keep the two
 * in step. The downgrade is applied unconditionally here (not just for
 * unverified claims) because the console has no way to tell a verified `fixed`
 * from an unverified one — `resolvedAgainstTs` isn't part of the client
 * `Resolution` type — and every resolution saved through this UI is a manual,
 * unverified claim anyway.
 *
 * A passing cell counts for its STATUS, and for nothing else, matching the
 * server: the two resolutions a pass can carry — a confirmed fix, and an AI fix
 * whose review is outstanding — are both worth saying on the row, and neither is
 * an open finding.
 */
export function patchLedgerQuestionResolution(
    row: LedgerQuestion, ruleId: string, resolution: Resolution | undefined,
): LedgerQuestion {
    const cell = row.cells[ruleId];
    if (!cell) return row; // this row doesn't carry that rule — nothing to patch
    const cells = { ...row.cells, [ruleId]: { ...cell, resolution } };

    let resolutionStatus: ResolutionStatus = 'not-an-issue';
    let activeCount = 0;
    let resolvedPassCount = 0;
    for (const c of Object.values(cells)) {
        if (c.verdict === 'pass') {
            if (c.resolution) {
                resolvedPassCount++;
                if (RESOLUTION_STATUSES.indexOf(c.resolution.status) < RESOLUTION_STATUSES.indexOf(resolutionStatus)) {
                    resolutionStatus = c.resolution.status;
                }
            }
            continue;
        }
        const effective: ResolutionStatus = c.resolution?.status === 'fixed' ? 'active' : (c.resolution?.status ?? 'active');
        if (effective === 'active') activeCount++;
        // Earlier in RESOLUTION_STATUSES = higher rank (worst-first order).
        if (RESOLUTION_STATUSES.indexOf(effective) < RESOLUTION_STATUSES.indexOf(resolutionStatus)) {
            resolutionStatus = effective;
        }
    }
    return { ...row, cells, resolutionStatus, activeCount, resolvedPassCount };
}

export interface FilterOptions {
    subject: string;
    topics: string[];
    boards: string[];
    grades: string[];
}

export const questionQualityApi = {
    /**
     * The whole judged corpus for one subject, grouped by question.
     *
     * One read behind the entire tab. It replaced three — a per-rule summary, a
     * flagged-question list and a verdict list — which each scanned the same
     * partition and each answered with a DIFFERENT population, so a filter
     * applied to one view had no meaning for the other.
     */
    ledger: (subject = 'chemistry', opts: {
        page?: number; pageSize?: number; filters?: Record<string, string>;
    } = {}) => {
        const params = new URLSearchParams({ subject });
        if (opts.page && opts.page > 1) params.set('page', String(opts.page));
        if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
        for (const [k, v] of Object.entries(opts.filters ?? {})) if (v) params.set(k, v);
        return fetchWithAdmin<LedgerResponse>(`/console/question-quality/ledger?${params}`);
    },

    /** Every value the filters can offer for a subject. Its own read because
     *  options derived from the returned rows are a function of the page. */
    filterOptions: (subject: string) =>
        fetchWithAdmin<FilterOptions>(
            `/console/question-quality/filter-options?subject=${encodeURIComponent(subject)}`),

    /** Record what a human decided about one finding (question × rule). The one
     *  write on this ledger — everything else is produced offline. */
    setResolution: (body: {
        subject: string; questionId: string; ruleId: string;
        status: ResolutionStatus; comment?: string;
    }) => fetchWithAdmin<{ resolution?: Resolution }>('/console/question-quality/resolution', {
        method: 'POST',
        body: JSON.stringify(body),
    }),

    /** Every verdict on one question, with its stem — the findings page. */
    byQuestion: (questionId: string, subject?: string) =>
        fetchWithAdmin<{
            questionId: string;
            questionText?: string;
            questionType?: string;
            moduleId?: string;
            /** Set when a teacher authored it — the Questions page scopes those
             *  separately, so a link out has to say which view to open. */
            teacherId?: string;
            /** The question's own subject. The `subject` argument below is only
             *  a partition hint for a question that no longer exists — trust
             *  this one for anything a reader sees. */
            subject?: string;
            verdicts: QualityVerdict[];
        }>(
            `/console/question-quality/by-question/${encodeURIComponent(questionId)}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`),
};
